import { moneyLabel, numberLabel } from "./consolidationPresentation.js";
import { consolidationMillKey } from "./consolidationMillCalculation.js";
import { formatConsolidationHours } from "./consolidationCalculationPresentation.js";
import { billedPartyLabel } from "./consolidationBillingParty.js";

export const prestationLabels = {
  loading: "Chargement", delivery: "Trajet et livraison", inspection: "Inspection", return: "Retour",
  preload: "Préchargement", tonne_supplement: "Supplément à la tonne", voyage_supplement: "Supplément voyage",
  fixed_fee: "Tarif fixe", dompeur: "Transport à la tonne",
};
export const summaryStatusLabels = { eligible: "Disponible", allocated: "Dans un relevé", blocked: "À compléter" };
export const summaryTrucks = row => row.truck_numbers?.length ? row.truck_numbers : row.truck_id ? [row.truck_id] : [];
export const prestationLabel = row => row.charge?.fixed_fee_source === "aspiration" ? "Tarif fixe · Aspiration"
  : prestationLabels[row.charge?.kind] || row.description || "Prestation";
export const summaryAmount = value => value == null ? "À déterminer" : moneyLabel(value);
export const summaryQuantity = row => row.unit === "heure" ? formatConsolidationHours(row.quantity)
  : row.unit === "tonne" ? `${numberLabel(row.quantity, 3)} t` : `${numberLabel(row.quantity)} ${row.unit || ""}`;

export function summaryTotals(rows) {
  const known = rows.filter(row => row.amount != null);
  const hours = rows.filter(row => row.unit === "heure").reduce((sum, row) => sum + Number(row.quantity), 0);
  // Line amounts are already rounded by the billing engine; sum their cents.
  const amount = known.reduce((sum, row) => sum + Math.round(Number(row.amount) * 100), 0) / 100;
  return { hours: formatConsolidationHours(hours),
    amount: rows.length && !known.length ? "À déterminer" : `${moneyLabel(amount)}${known.length < rows.length ? " · Total partiel" : ""}`,
    counts: Object.fromEntries(Object.keys(summaryStatusLabels).map(status => [status, rows.filter(row => row.status === status).length])),
  };
}

const normalize = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");

export function filterSummaryRows(rows, query, status) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  return rows.filter(row => (!status || row.status === status) && words.every(word => normalize([
    row.mill?.display_name, row.mill?.name, row.mill?.address, row.mill?.account_name, row.driver_name, ...summaryTrucks(row),
    row.description, row.charge?.order_label, row.date, row.execution_date,
  ].join(" ")).includes(word)));
}

export function groupSummaryRows(rows) {
  const mills = new Map();
  for (const row of rows) {
    const key = consolidationMillKey(row.mill);
    if (!mills.has(key)) mills.set(key, { key, mill: row.mill || {}, rows: [], drivers: new Map(), preloads: new Map() });
    const mill = mills.get(key);
    mill.rows.push(row);
    const preload = row.source_type === "preload";
    // A missing employee ID must not merge homonyms or infer a driver from a truck.
    const sourceKey = row.source_object_id || row.source_revision_id || row.id;
    const groupKey = preload ? sourceKey : row.employee_id || sourceKey;
    const groups = preload ? mill.preloads : mill.drivers;
    if (!groups.has(groupKey)) groups.set(groupKey, { key: groupKey, rows: [],
      label: preload ? `Préchargement du ${row.execution_date || row.date}${row.driver_name ? ` · ${row.driver_name}` : ""}`
        : row.driver_name || "Chauffeur non renseigné" });
    groups.get(groupKey).rows.push(row);
  }
  const names = new Map();
  for (const group of mills.values()) names.set(billedPartyLabel(group.mill), (names.get(billedPartyLabel(group.mill)) || 0) + 1);
  return [...mills.values()].map(group => ({ ...group, label: billedPartyLabel(group.mill), sameName: names.get(billedPartyLabel(group.mill)) > 1,
    drivers: [...group.drivers.values()].sort((a, b) => a.label.localeCompare(b.label, "fr")),
    preloads: [...group.preloads.values()].sort((a, b) => a.label.localeCompare(b.label, "fr")),
    reasons: [...new Set(group.rows.flatMap(row => row.status === "blocked" ? (row.blocking_reasons || []).map(reason => reason.message).filter(Boolean) : []))],
  })).sort((a, b) => a.label.localeCompare(b.label, "fr") || (a.mill.address || "").localeCompare(b.mill.address || "", "fr"));
}

export function summarySourceUrl(row) {
  if (!row.source_object_id) return null;
  const day = row.source_date || row.date;
  const params = new URLSearchParams({ from_date: day, to_date: day, view: "sheets" });
  return `/consolidation?${params}#${row.source_type === "preload" ? "preload" : "sheet"}-${row.source_object_id}`;
}

export function toggleSummarySelection(selected, rows, checked) {
  const ids = new Set(rows.filter(row => row.status === "eligible").map(row => row.id));
  return checked ? [...new Set([...selected, ...ids])] : selected.filter(id => !ids.has(id));
}
